import React, { ReactNode } from "react";

import Container from "../atoms/Container";
import FeatureCard from "../molecules/FeatureCard";
import { getYearsInBusiness } from "../../data/brand";

type Feature = {
  title: string;
  body: string;
  icon: ReactNode;
};

function Icon({ d }: { d: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

export const FEATURES: Feature[] = [
  {
    title: "Orders & inventory",
    body: "Track every order from intake to delivery, with real-time inventory visibility across products and warehouses.",
    icon: <Icon d="M3 7h18M3 12h18M3 17h12" />,
  },
  {
    title: "Customers & sales reps",
    body: "Keep customer profiles, sales-rep assignments, and account history in one searchable place — no more spreadsheets.",
    icon: (
      <Icon d="M16 14a4 4 0 1 0-8 0M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21a8 8 0 0 1 16 0" />
    ),
  },
  {
    title: "Carrier logistics",
    body: "Manage carrier companies and shipments with the context your operations team actually needs to make decisions.",
    icon: (
      <Icon d="M3 7h11v10H3zM14 10h4l3 3v4h-7zM6 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM18 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
    ),
  },
  {
    title: "Communication built in",
    body: "Phone, SMS, and email reach your customers from inside Digar — powered by Twilio and SendGrid, no extra tabs.",
    icon: (
      <Icon d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    ),
  },
  {
    title: "Secure by default",
    body: "JWT authentication and role-based access keep customer data, orders, and rep activity locked to the right people.",
    icon: <Icon d="M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5z" />,
  },
  {
    title: "Reporting that follows the work",
    body: "See what's open, what shipped, who sold what — without exporting to a spreadsheet first.",
    icon: <Icon d="M3 3v18h18M7 15l4-4 4 4 5-5" />,
  },
];

export default function FeatureGrid() {
  return (
    <section className="pb-20 pt-10 md:pb-24 md:pt-12">
      <Container>
        <div className="mx-auto mb-12 max-w-2xl text-center">
          <h2 className="mb-3 text-3xl font-medium text-gray-900 dark:text-gray-100 sm:text-4xl">
            Everything operations needs, in one place.
          </h2>
          <p className="text-base text-gray-600 dark:text-gray-400">
            The tools Digar teams have trusted for {getYearsInBusiness()} years
            and counting.
          </p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <FeatureCard
              key={f.title}
              icon={f.icon}
              title={f.title}
              body={f.body}
            />
          ))}
        </div>
      </Container>
    </section>
  );
}
